// Cálculo de costes de una obra. Es trabajo determinista: la misma ficha da
// siempre los mismos totales, así que vive fuera de la interfaz y con pruebas.
//
// Las columnas calculadas de SQL (dbo.Obras) reproducen estas mismas fórmulas; si
// cambia una, cambia también el script de BD (OBRAS_HITOS_Y_COSTES.sql y 01_Obras_Esquema.sql).
import { daysBetween } from "./portfolio/portfolioShared";

export function asCostNumber(value) {
    const parsed = typeof value === "number" ? value : Number(value || 0);
    return Number.isFinite(parsed) ? parsed : 0;
}

// Redondeo a céntimos igual que SQL Server (DECIMAL(18,2)): el medio céntimo se aleja del
// cero. En coma flotante 279103,10 × 1,15 da 320968,56499999…: sin corregir ese error se
// redondeaba a ,56 mientras la BD guardaba ,57, y el céntimo arrastraba los totales.
export function roundCost(value) {
    if (!Number.isFinite(value)) return value;
    const rounded = (Math.sign(value) * Math.round(Number((Math.abs(value) * 100).toFixed(6)))) / 100;
    return rounded === 0 ? 0 : rounded; // sin -0
}

// CAPEX = coste m² ref. × superficie construida. Los tres valores forman un
// triángulo: con dos cualesquiera se calcula el que falta. El campo que se acaba
// de teclear (`changed`) manda siempre; se recalcula uno de los otros dos.
export const CAPEX_TRIANGLE_FIELDS = ["cM2", "supCons", "capex"];

// Qué campo se recalcula al tocar cada uno, por orden de preferencia.
// Al cambiar el CAPEX se conserva la superficie (dato físico) y se ajusta el
// coste de referencia; al tocar superficie o coste manda el producto.
const TRIANGLE_TARGETS = {
    cM2: ["capex", "supCons"],
    supCons: ["capex", "cM2"],
    capex: ["cM2", "supCons"],
};

const triangleValue = (record, field) => {
    const value = record[field];
    if (value === "" || value == null) return 0;
    return asCostNumber(value);
};

const computeTriangleField = (field, values) => {
    const { cM2, supCons, capex } = values;
    if (field === "capex") return cM2 > 0 && supCons > 0 ? roundCost(cM2 * supCons) : null;
    if (field === "cM2") return capex > 0 && supCons > 0 ? roundCost(capex / supCons) : null;
    if (field === "supCons") return capex > 0 && cM2 > 0 ? roundCost(capex / cM2) : null;
    return null;
};

// Devuelve { record, derived } con el campo del triángulo que se ha recalculado.
// Solo actúa cuando el usuario teclea uno de los tres (`changed`): sin él no toca nada,
// para que la ficha enseñe lo mismo que la BD y ningún dato se escriba sin una acción.
export function applyCapexTriangle(record, changed) {
    if (!CAPEX_TRIANGLE_FIELDS.includes(changed)) return { record, derived: [] };
    const values = {
        cM2: triangleValue(record, "cM2"),
        supCons: triangleValue(record, "supCons"),
        capex: triangleValue(record, "capex"),
    };
    for (const target of TRIANGLE_TARGETS[changed]) {
        const next = computeTriangleField(target, values);
        if (next == null || next <= 0) continue;
        if (roundCost(values[target]) === next) return { record, derived: [] };
        return { record: { ...record, [target]: next }, derived: [target] };
    }
    return { record, derived: [] };
}

// Cambio deducido del triángulo mientras se edita una ficha.
// `saved`: valores guardados de la obra. `typed`: lo que el usuario ha tecleado a mano en
// los campos del triángulo (sin los deducidos). Se parte SIEMPRE de lo guardado más lo
// tecleado: si se partiera también de lo deducido en la pulsación anterior, cada tecla
// multiplicaría sobre el resultado previo (5 -> 50 -> 500 acababa en un CAPEX x100).
// Devuelve { field, value } si hay un campo que deducir distinto de lo guardado, o null.
export function capexTriangleDraft(saved, typed, changed) {
    if (!CAPEX_TRIANGLE_FIELDS.includes(changed)) return null;
    const isTyped = (field) => Object.prototype.hasOwnProperty.call(typed, field);
    // Si el campo ya no está tecleado (se volvió a su valor guardado) no se deduce nada:
    // en una obra con datos guardados incoherentes aparecería un cambio fantasma.
    if (!isTyped(changed)) return null;
    const values = Object.fromEntries(CAPEX_TRIANGLE_FIELDS.map((field) => [
        field,
        triangleValue(isTyped(field) ? typed : saved, field),
    ]));
    // Lo tecleado a mano manda: solo se deduce un campo que el usuario no haya escrito.
    for (const target of TRIANGLE_TARGETS[changed].filter((field) => !isTyped(field))) {
        const next = computeTriangleField(target, values);
        if (next == null || next <= 0) continue;
        return roundCost(asCostNumber(saved[target])) === next ? null : { field: target, value: next };
    }
    return null;
}

// Totales de la ficha. `changed` es el campo que acaba de cambiar (para el
// triángulo del CAPEX); el resto de totales no dependen de quién los dispara.
export function recalculateDerivedCosts(record, changed) {
    const { record: base } = applyCapexTriangle(record, changed);
    const updated = { ...base };
    // Duracion de obra = dias entre inicio y apertura (campo calculado, no editable).
    if (updated.inicioObra && updated.apertura) updated.dias = daysBetween(updated.inicioObra, updated.apertura);
    const constructionArea = asCostNumber(updated.supCons);
    const presupuesto = asCostNumber(updated.presupuesto);
    const adicional = asCostNumber(updated.adicional);
    const licencias = asCostNumber(updated.licencias);
    const fachada = asCostNumber(updated.fachada);
    const almacen = asCostNumber(updated.almacExt);
    const mobiliario = asCostNumber(updated.costeMobiliario);
    const audiovisual = asCostNumber(updated.audiovisual);
    const variosBsk = asCostNumber(updated.variosBsk);
    const coefTrans = asCostNumber(updated.coefTrans);
    // Sin marked up (vacío o nulo) vale 1, igual que ISNULL(MarkedUpItx, 1) en SQL.
    const markedUpItx = updated.markedUpItx === "" || updated.markedUpItx == null ? 1 : asCostNumber(updated.markedUpItx);
    const aranceles = asCostNumber(updated.aranceles);
    const variosPaisCorp = asCostNumber(updated.variosPaisCorp);
    const ssGg = asCostNumber(updated.ssGg);
    const techTienda = asCostNumber(updated.techTienda);

    updated.costeObra = roundCost(presupuesto + adicional);
    // La fachada es gasto de obra civil: entra en el total (decisión 2026-09-23).
    updated.totalOc = roundCost(licencias + updated.costeObra + fachada + almacen);
    updated.euroM2Oc = constructionArea ? roundCost(updated.totalOc / constructionArea) : null;

    updated.subtotalBsk = roundCost(mobiliario + audiovisual + variosBsk);
    updated.transporteBsk = roundCost(updated.subtotalBsk * coefTrans);
    updated.totalBsk = roundCost(updated.subtotalBsk * (1 + coefTrans) * markedUpItx);
    updated.euroM2Bsk = constructionArea ? roundCost(updated.subtotalBsk * (1 + coefTrans) * markedUpItx / constructionArea) : null;

    // Los aranceles son gasto de país: entran en otros (decisión 2026-09-23).
    updated.totalOtros = roundCost(aranceles + variosPaisCorp + ssGg + techTienda);
    updated.totalInversion = roundCost(updated.totalOc + updated.totalBsk + updated.totalOtros);
    updated.euroM2Inv = constructionArea ? roundCost(updated.totalInversion / constructionArea) : null;
    updated.observacionesCalculo = asCostNumber(updated.capex) > 0 && updated.totalInversion <= asCostNumber(updated.capex)
        ? "ok"
        : "no ok";

    return updated;
}
